import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Button,
  Chip,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import intl from 'components/i18n/ReactIntlWrapper';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import Spinner from 'components/Layout/Spinner';
import DefaultActions from 'components/DataGrid/DefaultActions';
import { SCOPES } from 'common/permissions';
import { getUserEntity } from 'common/UserEntityProvider';
import InvoiceConfigurationDrawer from 'components/InvoiceConfiguration/InvoiceConfigurationDrawer';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

export type InvoiceConfiguration = {
  id: number;
  idCorporateEntity: number;
  countryCode: string;
  prefix?: string;
  numberFormat: string;
  separator: string;
  startingNumber: number;
  active: boolean;
  additionalSettings?: string;
  isDeleted: boolean;
};

const InvoiceConfigurationPage: React.FC = () => {
  const { invoiceApi }: Api = useApi();
  const toast = useToastMessageQueue();
  const entityType = getUserEntity();

  const [configurations, setConfigurations] = useState<InvoiceConfiguration[]>([]);
  const [corporateEntities, setCorporateEntities] = useState<any[]>([]);
  const [selectedEntity, setSelectedEntity] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [selectedConfig, setSelectedConfig] = useState<InvoiceConfiguration | null>(null);
  const [errors, setErrors] = useState({});

  const [paginationModel, setPaginationModel] = usePaginationPreference('invoiceConfiguration', 25);

  const SORT_STATE = {
    sorting: {
      sortModel: [
        {
          field: 'countryCode',
          sort: 'asc' as const,
        },
      ],
    },
  };

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: intl.get('invoiceConfig.table.id'),
      width: 80,
    },
    {
      field: 'countryCode',
      headerName: intl.get('invoiceConfig.table.countryCode'),
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} color="primary" size="small" variant="outlined" />
      ),
    },
    {
      field: 'prefix',
      headerName: intl.get('invoiceConfig.table.prefix'),
      width: 150,
      renderCell: (params) => params.value || intl.get('invoiceConfig.table.noPrefix'),
    },
    {
      field: 'numberFormat',
      headerName: intl.get('invoiceConfig.table.numberFormat'),
      width: 150,
    },
    {
      field: 'separator',
      headerName: intl.get('invoiceConfig.table.separator'),
      width: 100,
    },
    {
      field: 'startingNumber',
      headerName: intl.get('invoiceConfig.table.startingNumber'),
      width: 150,
      type: 'number',
    },
    {
      field: 'preview',
      headerName: intl.get('invoiceConfig.table.preview'),
      width: 200,
      renderCell: (params) => {
        const prefix = params.row.prefix || '';
        const separator = params.row.separator || '';
        const format = params.row.numberFormat || 'D8';
        const padding = parseInt(format.replace('D', ''), 10) || 8;
        const example = params.row.startingNumber || 1;
        const formattedNumber = String(example).padStart(padding, '0');
        const preview = `${prefix}${separator}${formattedNumber}`;
        return (
          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
            {preview}
          </Typography>
        );
      },
    },
    {
      field: 'active',
      headerName: intl.get('invoiceConfig.table.active'),
      width: 100,
      type: 'boolean',
      renderCell: (params) => (
        <Chip
          label={params.value ? intl.get('common.active') : intl.get('common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: intl.get('common.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          <DefaultActions
            row={params.row}
            entityType={entityType.entityType}
            onEdit={() => handleEdit(params.row)}
            onDelete={() => handleDelete(params.row.id)}
            editScopes={[SCOPES['corporateentity.edit']]}
            deleteScopes={[SCOPES['corporateentity.delete']]}
          />
        </Box>
      ),
    },
  ];

  // Fetch corporate entities on mount
  useEffect(() => {
    fetchCorporateEntities();
  }, []);

  // Fetch configurations when entity is selected
  useEffect(() => {
    if (selectedEntity) {
      fetchConfigurations(selectedEntity);
    } else {
      setConfigurations([]);
    }
  }, [selectedEntity]);

  const fetchCorporateEntities = async () => {
    try {
      setLoading(true);
      const response = await invoiceApi.apiVversionCorporateEntitySearchGet(
        '1',
        '',
        1,
        100,
        'name',
        'asc',
        [],
        {}
      );
      if (response.data.data?.queryResult) {
        setCorporateEntities(response.data.data.queryResult);
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  };

  const fetchConfigurations = async (entityId: number) => {
    try {
      setLoading(true);
      const response = await invoiceApi.apiVversionInvoiceConfigurationEntityEntityIdGet(entityId, '1');
      if (response.data.data) {
        setConfigurations(response.data.data as InvoiceConfiguration[]);
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setConfigurations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedConfig(null);
    setDrawerOpen(true);
  };

  const handleEdit = (config: InvoiceConfiguration) => {
    setSelectedConfig(config);
    setDrawerOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(intl.get('invoiceConfig.confirmDelete'))) {
      return;
    }

    try {
      setLoading(true);
      await invoiceApi.apiVversionInvoiceConfigurationIdDelete(id, '1');
      toast.success({ body: intl.get('invoiceConfig.deleteSuccess') });
      if (selectedEntity) {
        fetchConfigurations(selectedEntity);
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (config: Partial<InvoiceConfiguration>) => {
    try {
      setLoading(true);
      if (selectedConfig?.id) {
        await invoiceApi.apiVversionInvoiceConfigurationIdPut(selectedConfig.id, '1', {
          prefix: config.prefix,
          numberFormat: config.numberFormat || 'D8',
          separator: config.separator || '-',
          startingNumber: config.startingNumber || 1,
          active: config.active ?? true,
          additionalSettings: config.additionalSettings,
        });
        toast.success({ body: intl.get('invoiceConfig.updateSuccess') });
      } else {
        await invoiceApi.apiVversionInvoiceConfigurationPost('1', {
          idCorporateEntity: selectedEntity!,
          countryCode: config.countryCode!,
          prefix: config.prefix,
          numberFormat: config.numberFormat || 'D8',
          separator: config.separator || '-',
          startingNumber: config.startingNumber || 1,
          active: config.active ?? true,
          additionalSettings: config.additionalSettings,
        });
        toast.success({ body: intl.get('invoiceConfig.createSuccess') });
      }
      setDrawerOpen(false);
      if (selectedEntity) {
        fetchConfigurations(selectedEntity);
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography color="primary" variant="h2" component="h2" sx={{ mb: 3 }}>
        {intl.get('invoiceConfig.title')}
      </Typography>

      <Card>
        <Box sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>{intl.get('invoiceConfig.selectEntity')}</InputLabel>
            <Select
              value={selectedEntity || ''}
              onChange={(e) => setSelectedEntity(Number(e.target.value) || null)}
              label={intl.get('invoiceConfig.selectEntity')}
            >
              <MenuItem value="">
                <em>{intl.get('common.none')}</em>
              </MenuItem>
              {corporateEntities.map((entity) => (
                <MenuItem key={entity.id} value={entity.id}>
                  {entity.name} ({entity.country?.labelKey ? intl.get(entity.country.labelKey) : entity.country?.name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedEntity && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
              disabled={!selectedEntity}
            >
              {intl.get('invoiceConfig.addConfiguration')}
            </Button>
          )}
        </Box>

        {loading && <Spinner />}

        {!loading && selectedEntity && (
          <Box sx={{ height: 600, p: 2 }}>
            <DataGrid
              rows={configurations}
              columns={columns}
              initialState={SORT_STATE}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              pageSizeOptions={[10, 25, 50, 100]}
              disableRowSelectionOnClick
              sx={{
                '& .MuiDataGrid-cell': {
                  py: 1,
                },
              }}
            />
          </Box>
        )}

        {!loading && !selectedEntity && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              {intl.get('invoiceConfig.selectEntityPrompt')}
            </Typography>
          </Box>
        )}
      </Card>

      <InvoiceConfigurationDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSave}
        config={selectedConfig}
        corporateEntityId={selectedEntity}
      />
    </Box>
  );
};

export default InvoiceConfigurationPage;
